import React, { useEffect, useState } from 'react';
import { ENROLLMENT_URL, listCourses } from '../../Services/courseService';
import CourseTemplate from '../CourseSections/CourseTemplate';
import ClipLoader from 'react-spinners/ClipLoader';
import axios from 'axios';
import './Courses.css';
import { useTranslation } from 'react-i18next';

const Courses = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [signalStrength, setSignalStrength] = useState(0);
  const [ping, setPing] = useState(null);
  const { t } = useTranslation();

  const userId = localStorage.getItem('userId');

  const fetchEnrollments = async () => {
    if (!userId) return [];
    try {
      const enrollmentResponse = await axios.get(`${ENROLLMENT_URL}/${userId}/enrollments`);
      return enrollmentResponse.data.map(enrollment => enrollment.courseId);
    } catch (error) {
      console.error('Error fetching enrollments:', error);
      return [];
    }
  };

  const generateSlug = (title) =>
    title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const response = await listCourses();
        const allCourses = Array.isArray(response?.data?.data) ? response?.data?.data : [];

        const slugToIdMap = {};

        const coursesWithSlug = allCourses.map(course => {
          const slug = generateSlug(course.title);
          slugToIdMap[slug] = course.courseId;
          return { ...course, slug };
        });

        // Save slug:courseId map to localStorage
        localStorage.setItem('slugCourseMap', JSON.stringify(slugToIdMap));

        if (userId) {
          const enrolledCourseIds = await fetchEnrollments();
          const availableCourses = coursesWithSlug.filter(course => !enrolledCourseIds.includes(course.courseId));
          setFilteredCourses(availableCourses);
        } else {
          setFilteredCourses(coursesWithSlug);
        }
      } catch (error) {
        setError('Error fetching courses');
        console.error('Error fetching courses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCourses();
  }, [userId]);

  const measurePing = async () => {
    const start = Date.now();
    try {
      await fetch("https://www.google.com/favicon.ico", { method: 'HEAD', mode: 'no-cors' });
      const latency = Date.now() - start;
      setPing(latency);
    } catch {
      setPing(null);
    }
  };

  useEffect(() => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    const calculateSignal = (downlink) => {
      if (!navigator.onLine || downlink === 0 || downlink == null) return 0;
      if (downlink >= 10) return 4;
      if (downlink >= 5) return 3;
      if (downlink >= 2) return 2;
      if (downlink >= 0.5) return 1;
      return 0;
    };

    const updateSignalAndPing = () => {
      const strength = calculateSignal(connection?.downlink);
      setSignalStrength(strength);
      measurePing();
    };

    updateSignalAndPing();

    if (connection) {
      connection.addEventListener('change', updateSignalAndPing);
    }

    window.addEventListener('online', updateSignalAndPing);
    window.addEventListener('offline', updateSignalAndPing);

    const intervalId = setInterval(updateSignalAndPing, 10000);

    return () => {
      if (connection) {
        connection.removeEventListener('change', updateSignalAndPing);
      }
      window.removeEventListener('online', updateSignalAndPing);
      window.removeEventListener('offline', updateSignalAndPing);
      clearInterval(intervalId);
    };
  }, []);

  const renderSignalBars = () => (
    <div style={{
      position: 'fixed',
      bottom: '10px',
      right: '20px',
      display: 'flex',
      alignItems: 'flex-end',
      zIndex: 1000,
      backgroundColor: 'rgba(255,255,255,0.9)',
      padding: '8px 12px',
      borderRadius: '10px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
      userSelect: 'none',
      fontFamily: 'Arial, sans-serif'
    }}>
      {[1, 2, 3, 4].map((level) => (
        <div
          key={level}
          style={{
            width: '6px',
            height: `${level * 8 + 4}px`,
            marginRight: '5px',
            backgroundColor: level <= signalStrength ? (signalStrength < 2 ? 'red' : '#4caf50') : '#ddd',
            borderRadius: '2px',
            transition: 'background-color 0.3s ease'
          }}
        />
      ))}
      <span style={{ marginLeft: '12px', fontSize: '0.9rem', color: '#333', whiteSpace: 'nowrap' }}>
        {ping !== null ? `Ping: ${ping}ms` : 'Ping: --'}
      </span>
    </div>
  );

  return (
    <div style={{ position: 'relative' }}>
      {loading ? (
        <ClipLoader color="#333333" loading={loading} size={30} id="clipLoader" />
      ) : filteredCourses?.length > 0 ? (
        <div className="course-main-container">
          <CourseTemplate
            coursesData={filteredCourses}
            pageTitle={t("Courses")}
            error={error}
          />
        </div>
      ) : (
        <div className="course-not-found-main-container">
          <h2>Exciting courses are coming soon!</h2>
          <h2>We don't have new courses at the moment. Please check back later.</h2>
        </div>
      )}
      {/* {renderSignalBars()} */}
    </div>
  );
};

export default Courses;
